import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from 'react';
import { User, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { SessionService } from '../services/SessionService';
import { UserRole } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  userRole: UserRole | null;
  isAdmin: boolean;
  authError: string | null;
  isSMSVerificationPending: boolean; // NEW: Expose SMS verification state
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  userRole: null,
  isAdmin: false,
  authError: null,
  isSMSVerificationPending: false,
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSMSVerificationPending, setIsSMSVerificationPending] =
    useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log(
        'Auth state changed:',
        user ? `User ${user.email}` : 'No user'
      );

      // Check if SMS verification is in progress
      const smsVerificationPending =
        sessionStorage.getItem('sms_verification_pending') === '1';
      setIsSMSVerificationPending(smsVerificationPending);

      if (!user) {
        console.log('Auth state changed: No user');

        // Clear user state even during SMS verification
        // Note: authError is intentionally NOT cleared here — it may have
        // just been set before the signOut that triggered this callback.
        // It will be cleared on the next successful sign-in (line below setAuthError(null)).
        setUser(null);
        setUserRole(null);
        setIsAdmin(false);
        setLoading(false);

        // If SMS verification is pending, just keep UI stable (no redirects)
        // The flag tells AuthNavigator not to redirect
        if (smsVerificationPending) {
          console.log('SMS verification pending, keeping UI stable');
        }

        return;
      }

      console.log('Auth state changed: User', user.email);

      // 🔑 If SMS verification is pending, do minimal setup
      // This prevents race conditions with LoginPage's SMS 2FA flow
      // But still maintains user state for proper navigation after verification
      if (smsVerificationPending) {
        console.log(
          'SMS verification pending, skipping full auth initialization'
        );
        setUser(user); // Keep user set so auth state is maintained
        setUserRole(null); // But don't load role/Firestore data yet
        setIsAdmin(false);
        setAuthError(null);
        setLoading(false);
        return; // Skip email verification, Firestore loading, etc.
      }

      setAuthError(null);

      // 🔑 CHECK SETUP FLAG - Allow users during account creation
      const allowUnverifiedSetup = sessionStorage.getItem(
        'allow_unverified_setup'
      );

      if (allowUnverifiedSetup === '1') {
        console.log(
          'User is in setup flow, allowing missing Firestore document'
        );
        setUser(user);
        setUserRole(null);
        setIsAdmin(false);
        setLoading(false);
        return; // Don't check Firestore yet, document is being created
      }

      // Skip email verification check if user just completed SMS 2FA
      // (they already verified email when enabling 2FA)
      const skipEmailCheck = sessionStorage.getItem('skip_email_check') === '1';

      if (!user.emailVerified && !skipEmailCheck) {
        console.warn(
          'User attempted login without verified email:',
          user.email
        );
        setAuthError(
          'Your email has not been verified yet. Please click the confirmation link we sent to your email address. If you cannot find it, please check your spam or junk folder.'
        );

        try {
          await signOut(auth);
        } catch (e) {
          // ignore
        }

        setUser(null);
        setUserRole(null);
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        // Load role from Firestore
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const data = userDocSnap.data() as any;
          const role = (data.role ?? null) as UserRole | null;

          setUser(user);
          setUserRole(role);
          setIsAdmin(role === 'admin');
        } else {
          console.log('No Firestore document found for user', user.uid);

          // User has no Firestore document and is NOT in setup flow
          console.error(
            'User has no Firestore document and is not in setup flow'
          );
          setAuthError(
            'Your account profile was not found (it may have been deleted). Please sign up again or contact support.'
          );

          await signOut(auth);
          setUser(null);
          setUserRole(null);
          setIsAdmin(false);
        }
      } catch (err) {
        console.error('AuthContext error:', err);
        setAuthError('Failed to load account profile. Please try again.');
        await signOut(auth);
        setUser(null);
        setUserRole(null);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Monitor page visibility for activity tracking
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && user) {
        try {
          // Page became visible - update activity timestamp
          await SessionService.updateLastActivity();
        } catch (error) {
          console.error('[Auth] Error updating activity:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        userRole,
        isAdmin,
        authError,
        isSMSVerificationPending,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
